// common/stores/uiStore.js
import { makeObservable, observable, action, computed, autorun } from "mobx";
import { EVENTS, storeEvents } from "./storeEvents.js";
import authStore from './authStore.js';
import contextStore from './contextStore.js';
import { Tooltip } from "bootstrap";
import { logger } from '../../utils/debug/logger.js';

class UIStore {
  // États observables
  isDataLoaded = false;
  isFanChartDrawn = false;
  activeTab = "Fan";
  
  constructor() {
    logger.initialization.ui.info('🚀 UIStore initializing');
    
    makeObservable(this, {
      isDataLoaded: observable,
      isFanChartDrawn: observable,
      activeTab: observable,
      
      setDataLoaded: action,
      setFanChartDrawn: action,
      setActiveTab: action,
      resetToDefaults: action,
      
      areExportOptionsEnabled: computed,
      areToolsEnabled: computed
    });
    
    // S'abonner aux événements
    this.setupEventListeners();
    
    // Observer les changements et mettre à jour le DOM
    this.setupAutorun();
  }
  
  setupEventListeners() {
    
    // Utilisation de la chaîne directe plutôt que la constante pour debug
    storeEvents.subscribe('visualization:fan:drawn', (data) => {
      this.setFanChartDrawn(true);
    });
    
    storeEvents.subscribe(EVENTS.ONBOARDING.GEDCOM_UPLOADED, (data) => {
      this.setDataLoaded(true);
    });
    
  }
  
  setupAutorun() {
    // 🔁 Autorun pour les contrôles UI (déjà existant)
    autorun(() => {
      this.updateUIControls();
    });
  
    autorun(() => {
      // The avatar is the dropdown toggler. It carries the green/grey
      // auth-state colour and an email tooltip. The "Se déconnecter"
      // item only makes sense for guests (auth-service __authsvc) —
      // they own their session and can clear the cookie. Owners
      // authenticate through the integrator site so there is no local
      // action to take; we hide the item and its divider for them.
      const toggle = document.getElementById('user-menu-toggle');
      const emailHeader = document.getElementById('user-menu-email-header');
      const emailDivider = document.getElementById('user-menu-email-divider');
      const signInItem = document.getElementById('sign-in-button');
      const signInWrapper = document.getElementById('sign-in-button-wrapper');
      const signInDivider = document.getElementById('sign-in-divider');
      const signInLabel = document.getElementById('sign-in-label');
      const signInIcon = document.getElementById('sign-in-icon');
      if (!toggle) return;

      const isGuest = authStore.authMode === 'guest';
      const showLogout = isGuest && authStore.isAuthenticated;

      if (authStore.isAuthenticated) {
        const email = authStore.userInfo?.email || 'Utilisateur connecté';
        toggle.classList.add('btn-success');
        toggle.setAttribute('title', email);
        toggle.style.opacity = contextStore.isInIframe ? '0.7' : '';

        if (emailHeader) {
          emailHeader.textContent = email;
          emailHeader.style.display = '';
        }
        if (emailDivider) emailDivider.style.display = '';
      } else {
        toggle.classList.remove('btn-success');
        toggle.setAttribute('title', 'Menu');
        toggle.style.opacity = contextStore.isInIframe ? '0.5' : '';

        if (emailHeader) {
          emailHeader.textContent = '';
          emailHeader.style.display = 'none';
        }
        if (emailDivider) emailDivider.style.display = 'none';
      }

      if (signInWrapper) signInWrapper.style.display = showLogout ? '' : 'none';
      if (signInDivider) signInDivider.style.display = showLogout ? '' : 'none';
      if (showLogout) {
        if (signInLabel) signInLabel.textContent = 'Se déconnecter';
        if (signInIcon) signInIcon.className = 'fas fa-sign-out-alt me-2';
        signInItem?.classList.remove('disabled');
      }

      // Re-init tooltips (the toggler title may have changed).
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        new Tooltip(tooltipTriggerEl);
      });
    });
  }
  
  updateUIControls() {
    // Mise à jour des éléments d'exportation
    
    const exportItems = [
      'download-pdf-watermark',
      'download-pdf',
      'download-svg',
      'download-png-transparency',
      'download-png-background'
    ].map(id => document.getElementById(id));
    
    exportItems.forEach(item => {
      if (item) {
        if (this.areExportOptionsEnabled) {
          item.classList.remove('disabled');
        } else {
          item.classList.add('disabled');
        }
      }
    });
    
    // Mise à jour des boutons d'interface
    const toolsButton = document.getElementById('toolsButton');
    
    if (toolsButton) {
      const wasDisabled = toolsButton.disabled;
      toolsButton.disabled = !this.areToolsEnabled;
      if (wasDisabled !== toolsButton.disabled) {
      }
    }
    
  }
  
  // Actions
  setDataLoaded(isLoaded) {
    this.isDataLoaded = isLoaded;
  }
  
  setFanChartDrawn(isDrawn) {
    this.isFanChartDrawn = isDrawn;
  }
  
  setActiveTab(tabName) {
    this.activeTab = tabName;
  }

  // Méthode pour réinitialiser l'état UI aux valeurs par défaut
  resetToDefaults() {
    logger.initialization.ui.debug('🔄 Resetting UIStore to defaults');
    this.isDataLoaded = false;
    this.isFanChartDrawn = false;
    this.activeTab = "Fan";
  }
  
  // Computed properties
  get areExportOptionsEnabled() {
    return this.isFanChartDrawn;
  }
  
  get areToolsEnabled() {
    return this.isFanChartDrawn;
  }
}

// Singleton instance
const uiStore = new UIStore();

// Débogage pour vérifier l'état initial

// Pour permettre une utilisation immédiate même si l'import se fait tardivement
if (typeof window !== 'undefined') {
  window.__DEBUG_UI_STORE = uiStore;
}

export default uiStore;